import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FaPlus,
  FaSearch,
  FaTh,
  FaList,
  FaFilter,
  FaSortAmountDown,
} from "react-icons/fa";
import toast from "react-hot-toast";
import TaskKanbanBoard from "../components/tasks/TaskKanbanBoard";
import TaskListView from "../components/tasks/TaskListView";
import TaskDetailModal from "../components/tasks/TaskDetailModal";
import {
  fetchTasks,
  fetchTaskById,
  fetchTaskStatuses,
  fetchTeamContacts,
  getNextTaskPosition,
  logTaskActivity,
  updateTask,
} from "../api/pocketbase";
import { getAuthUser, getRelationId, PRIORITIES, TASK_CATEGORIES, sortTasks, sortTasksInColumn } from "../utils/taskHelpers";
import {
  fireTaskNotification,
  notifyTaskStatusChanged,
} from "../utils/taskEmailService";

const SORT_OPTIONS = [
  { value: "+Position,+created", label: "Board Order" },
  { value: "-created", label: "Newest First" },
  { value: "+created", label: "Oldest First" },
  { value: "-DueDate", label: "Due Date (Latest)" },
  { value: "+DueDate", label: "Due Date (Soonest)" },
  { value: "-Priority", label: "Priority (High → Low)" },
  { value: "+Priority", label: "Priority (Low → High)" },
  { value: "-updated", label: "Recently Updated" },
];

const TasksPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [viewMode, setViewMode] = useState("board");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [sortOrder, setSortOrder] = useState("-Priority");

  const [selectedTask, setSelectedTask] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [dragOverStatusId, setDragOverStatusId] = useState(null);
  const [draggedTaskId, setDraggedTaskId] = useState(null);

  const authUser = getAuthUser();

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    try {
      const [statusData, contactData, taskData] = await Promise.all([
        fetchTaskStatuses(),
        fetchTeamContacts(),
        fetchTasks({
          includeArchived: showArchived,
          search,
          priority: priorityFilter,
          assignee: assigneeFilter,
          statusId: statusFilter,
          category: categoryFilter,
        }),
      ]);
      setStatuses(statusData);
      setContacts(contactData);
      setTasks(taskData);
    } catch (error) {
      console.error("Error loading tasks:", error);
      toast.error(error?.message || "Failed to load tasks");
      if (!silent) setTasks([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [search, priorityFilter, assigneeFilter, statusFilter, categoryFilter, showArchived]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sortedTasks = useMemo(
    () => sortTasks(tasks, sortOrder, statuses),
    [tasks, sortOrder, statuses]
  );

  const boardStatuses = useMemo(() => {
    if (!statusFilter) return statuses;
    return statuses.filter((s) => s.id === statusFilter);
  }, [statuses, statusFilter]);

  useEffect(() => {
    const openTaskId = searchParams.get("task");
    if (!openTaskId || isLoading) return;

    const openFromList = tasks.find((t) => t.id === openTaskId);
    if (openFromList) {
      setSelectedTask(openFromList);
      setSearchParams({}, { replace: true });
      return;
    }

    fetchTaskById(openTaskId)
      .then((task) => {
        setSelectedTask(task);
        setSearchParams({}, { replace: true });
      })
      .catch(() => {});
  }, [searchParams, tasks, isLoading, setSearchParams]);

  const tasksByStatus = useMemo(() => {
    const grouped = {};
    boardStatuses.forEach((status) => {
      grouped[status.id] = [];
    });
    sortedTasks.forEach((task) => {
      const statusId = getRelationId(task.Status) || getRelationId(task.expand?.Status);
      if (statusId && grouped[statusId]) {
        grouped[statusId].push(task);
      }
    });
    Object.keys(grouped).forEach((statusId) => {
      grouped[statusId] = sortTasksInColumn(grouped[statusId], sortOrder);
    });
    return grouped;
  }, [sortedTasks, boardStatuses, sortOrder]);

  const handleDragStart = (e, task) => {
    setDraggedTaskId(task.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", task.id);
  };

  const handleDragOver = (e, statusId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStatusId(statusId);
  };

  const handleDragLeave = () => {
    setDragOverStatusId(null);
  };

  const handleDrop = async (e, targetStatusId) => {
    e.preventDefault();
    setDragOverStatusId(null);

    const taskId = e.dataTransfer.getData("text/plain") || draggedTaskId;
    if (!taskId) return;

    const task = tasks.find((t) => t.id === taskId);
    if (!task || getRelationId(task.Status) === targetStatusId) {
      setDraggedTaskId(null);
      return;
    }

    const oldStatus = statuses.find((s) => s.id === getRelationId(task.Status));
    const newStatus = statuses.find((s) => s.id === targetStatusId);
    const position = await getNextTaskPosition(targetStatusId);

    try {
      await updateTask(taskId, { Status: targetStatusId, Position: position });
      await logTaskActivity({
        taskId,
        actorId: authUser?.id,
        action: "status_changed",
        message: `${authUser?.Name || "Someone"} moved task from ${oldStatus?.Name} to ${newStatus?.Name}`,
        metadata: { from: oldStatus?.Name, to: newStatus?.Name },
      });
      fireTaskNotification(() =>
        notifyTaskStatusChanged({
          task: { ...task, Status: targetStatusId },
          contacts,
          statuses,
          actorId: authUser?.id,
          oldStatusName: oldStatus?.Name || "",
          newStatusName: newStatus?.Name || "",
        })
      );
      toast.success(`Moved to ${newStatus?.Name}`);
      await loadData({ silent: true });
    } catch (error) {
      console.error("Error moving task:", error);
      toast.error("Failed to move task");
    } finally {
      setDraggedTaskId(null);
    }
  };

  const handleOpenTask = (task) => setSelectedTask(task);
  const handleCloseModal = () => {
    setSelectedTask(null);
    setIsCreateOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="flex flex-col gap-4 rounded-bl-3xl rounded-br-3xl bg-white px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-6 md:px-10 sm:py-6 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">Tasks</h1>
          <p className="mt-1 text-sm text-gray-500">Internal team task manager</p>
        </div>
        <button
          type="button"
          onClick={() => setIsCreateOpen(true)}
          disabled={isLoading || statuses.length === 0}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#90c740] px-4 py-2.5 text-sm font-medium text-white transition hover:bg-[#7ebd2e] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FaPlus /> New Task
        </button>
      </header>

      <main className="flex-1 px-2 sm:px-4 md:px-10 pb-10 w-full">
        <div className="rounded-xl bg-white shadow-sm overflow-hidden">
          <div className="border-b border-gray-100 p-4 sm:p-6 space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative flex-1 max-w-md">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search tasks..."
                  className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#90c740]"
                />
              </div>

              <div className="flex items-center gap-1 rounded-lg border border-gray-200 p-1">
                <button
                  type="button"
                  onClick={() => setViewMode("board")}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                    viewMode === "board" ? "bg-[#90c740] text-white" : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <FaTh className="text-xs" /> Board
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                    viewMode === "list" ? "bg-[#90c740] text-white" : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <FaList className="text-xs" /> List
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <FaFilter className="hidden text-gray-400 sm:block" />

              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#90c740]"
              >
                <option value="">All Priorities</option>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>

              <select
                value={assigneeFilter}
                onChange={(e) => setAssigneeFilter(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#90c740]"
              >
                <option value="">All Assignees</option>
                <option value="unassigned">Unassigned</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.Name} {c.Surname}
                  </option>
                ))}
              </select>

              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#90c740]"
              >
                <option value="">All Categories</option>
                {TASK_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#90c740]"
              >
                <option value="">All Statuses</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>{s.Name}</option>
                ))}
              </select>

              <div className="relative">
                <FaSortAmountDown className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#90c740]"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="rounded text-[#90c740]"
                />
                Show archived
              </label>
            </div>

            <p className="text-sm text-gray-500">
              {isLoading
                ? "Loading..."
                : `${sortedTasks.length} task${sortedTasks.length !== 1 ? "s" : ""} found${isRefreshing ? " · refreshing..." : ""}`}
            </p>
          </div>

          <div className="p-4 sm:p-6">
            {viewMode === "board" ? (
              isLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: Math.ceil(Math.max(statuses.length, 6) / 3) }).map((_, row) => (
                    <div key={row} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {Array.from({ length: 3 }).map((__, col) => (
                        <div key={col} className="h-64 animate-pulse rounded-xl bg-gray-100" />
                      ))}
                    </div>
                  ))}
                </div>
              ) : statuses.length === 0 ? (
                <div className="py-16 text-center text-gray-500">
                  <p className="font-medium">No task statuses found</p>
                  <p className="mt-1 text-sm">Add statuses in PocketBase (Task_Statuses collection)</p>
                </div>
              ) : (
                <TaskKanbanBoard
                  statuses={boardStatuses}
                  tasksByStatus={tasksByStatus}
                  onOpenTask={handleOpenTask}
                  onDragStart={handleDragStart}
                  onDrop={handleDrop}
                  dragOverStatusId={dragOverStatusId}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                />
              )
            ) : (
              <TaskListView tasks={sortedTasks} onOpenTask={handleOpenTask} isLoading={isLoading} />
            )}
          </div>
        </div>
      </main>

      {(selectedTask || isCreateOpen) && (
        <TaskDetailModal
          taskId={selectedTask?.id}
          isCreateMode={isCreateOpen}
          statuses={statuses}
          contacts={contacts}
          onClose={handleCloseModal}
          onSaved={async () => {
            await loadData({ silent: true });
          }}
        />
      )}
    </div>
  );
};

export default TasksPage;
